<template>
  <div class="market-page">
    <v-card outlined rounded="lg" class="mb-3 pa-4">
      <div class="market-header mb-3">
        <div class="orbitron-title">Mercado</div>

        <div class="market-budget">
          Presupuesto: {{ money(budget) }}
        </div>
      </div><br>

      <v-tabs v-model="tab" grow class="custom-tabs market-tabs">
        <v-tab>Generación</v-tab>

        <v-tab>
          <span class="tab-label-with-notification">
            Ventas
            <span v-if="ventasCount > 0">({{ ventasCount }})</span>
            <span v-if="ventasCount > 0" class="sales-notification-dot"></span>
          </span>
        </v-tab>
      </v-tabs>
    </v-card><br>
    <!-- Aviso de precios semanales -->
    <v-row class="mb-3">
      <v-col cols="12" md="6" class="py-0">
        <v-alert
          dense
          class="weekly-price-alert mb-0"
        >
          <span class="material-symbols-outlined mr-2">
            monitoring
          </span>

          Los precios mínimos se actualizan cada lunes según el rendimiento reciente de cada activo.
        </v-alert>
      </v-col>

      <v-col cols="12" md="6" class="py-0">
        <v-alert
          dense
          class="weekly-price-alert mb-0"
        >
          <span class="material-symbols-outlined mr-2">
            info
          </span>

          El mercado se cierra a las 15:00h de cada día, el nuevo se lanza a las 15:30h.
        </v-alert>
      </v-col>
    </v-row>

    <v-tabs-items v-model="tab" class="mt-4 market-content">
      <!-- TAB 0: Generación -->
      <v-tab-item>
        <GeneratorMarket
          :generators="generators"
          :budget="budget"
          :liga-id="activeLigaId"
          @bid="emitBid"
          @cancel-bid="emitCancelBid"
          @sell-market="emitCreateSale"
          @show-map="abrirMapaActivo"
          @show-history="abrirHistoricoActivo"
        />
      </v-tab-item>

      <!-- TAB 1: Ventas -->
      <v-tab-item eager>
        <MarketSales
          :budget="budget"
          :liga-id="activeLigaId"
          @refresh-budget="emitRefreshBudget"
          @sales-count="ventasCount = $event"
        />
      </v-tab-item>
    </v-tabs-items>

    <!-- Modal mapa activo -->
    <v-dialog v-model="mapDialog" max-width="900">
      <v-card rounded="lg">
        <v-card-title class="d-flex align-center justify-space-between">
          <div>
            Ubicación del activo
            <div class="text-caption grey--text">
              {{ selectedAsset ? selectedAsset.name : "" }}
            </div>
          </div>

          <v-btn
            icon
            class="map-close-btn"
            @click="cerrarMapaActivo"
          >
            <span class="map-close-x">×</span>
          </v-btn>
        </v-card-title>

        <v-card-text>
          <div v-if="selectedAsset" class="asset-map-info mb-2">
            <strong>{{ selectedAsset.name }}</strong>
            <span v-if="selectedAsset.type"> · {{ selectedAsset.type }}</span>
            <span v-if="selectedAsset.capacity"> · {{ selectedAsset.capacity }} MW</span>
            <br>
            <span v-if="selectedAsset.municipio || selectedAsset.provincia">
              {{ selectedAsset.municipio || "—" }} / {{ selectedAsset.provincia || "—" }}
            </span>
          </div>

          <div ref="assetMap" class="asset-map-modal"></div>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Modal histórico activo -->
    <v-dialog v-model="historyDialog" max-width="560px">
      <v-card rounded="lg">
        <v-card-title class="headline grey lighten-4">
          <span class="modal-history-title">
            Histórico de ganancias
          </span>
        </v-card-title>

        <v-card-text class="pa-4">
          <v-progress-linear
            v-if="loadingHistory"
            indeterminate
            color="primary"
            class="mb-4"
          />

          <div
            v-if="!loadingHistory && assetHistory.length === 0"
            class="text-center py-4 grey--text"
          >
            Este activo todavía no tiene histórico registrado.
          </div>

          <div v-if="!loadingHistory && assetHistory.length > 0">
            <div class="text-caption grey--text mb-3">
              Últimas 5 ganancias registradas para este activo.
            </div>

            <v-simple-table dense>
              <thead>
                <tr>
                  <th>Fecha</th>
                  <th class="text-right">Ganancia</th>
                  <th class="text-right">Puntos</th>
                </tr>
              </thead>

              <tbody>
                <tr v-for="(h, index) in assetHistory" :key="index">
                  <td>{{ formatDate(h.fecha) }}</td>

                  <td class="text-right">
                    {{ moneyDecimal(h.ganancia_activo) }}
                  </td>

                  <td class="text-right">
                    <span
                      class="asset-history-points"
                      :class="Number(h.puntos_activo || 0) >= 0 ? 'green--text' : 'red--text'"
                    >
                      {{ formatPoints(h.puntos_activo) }}
                    </span>
                  </td>
                </tr>
              </tbody>
            </v-simple-table>
          </div>
        </v-card-text>

        <v-divider />

        <v-card-actions>
          <v-spacer />
          <v-btn color="primary" text @click="historyDialog = false">
            Cerrar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import GeneratorMarket from "@/components/GeneratorMarket.vue";
import MarketSales from "@/components/MarketSales.vue";
import { useGameStore } from "@/stores/game";
import Swal from "sweetalert2";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

export default {
  name: "GameMarket",

  components: {
    GeneratorMarket,
    MarketSales,
  },

  props: {
    generators: { type: Array, required: true },
    budget: { type: Number, default: 0 },
  },

 data() {
  return {
    store: useGameStore(),

    tab: 0,
    ventasCount: 0,

    mapDialog: false,
    selectedAsset: null,
    assetMap: null,
    assetMarker: null,

    historyDialog: false,
    assetHistory: [],
    loadingHistory: false,
    historyLoadingId: null,
  };
},
  computed: {
    activeLigaId() {
      const rawLigaId =
        this.store.currentAreaId ??
        this.store.player?.activeLigaId ??
        this.store.company?.player?.activeLigaId ??
        this.store.authUser?.active_liga_id ??
        this.store.authUser?.activeLigaId;

      const ligaId = Number(rawLigaId);

      return Number.isInteger(ligaId) && ligaId > 0
        ? ligaId
        : null;
    },
  },
  watch: {
    activeLigaId(newLigaId, oldLigaId) {
      if (newLigaId === oldLigaId) return;

      this.ventasCount = 0;
      this.historyDialog = false;
      this.assetHistory = [];
      this.cerrarMapaActivo();
    },
  },
  beforeUnmount() {
  if (this.assetMap) {
    this.assetMap.remove();
    this.assetMap = null;
  }
},

  methods: {
    requireActiveLigaId() {
      const ligaId = Number(this.activeLigaId);

      if (!Number.isInteger(ligaId) || ligaId <= 0) {
        throw new Error(
          "No se ha podido identificar la liga seleccionada. Vuelve a entrar en la liga."
        );
      }

      return ligaId;
    },

    withLiga(payload = {}) {
      return {
        ...payload,
        liga_id: this.requireActiveLigaId(),
      };
    },

    emitBid(payload) {
      this.$emit("bid-generator", this.withLiga(payload));
    },

    emitCancelBid(payload) {
      this.$emit("cancel-bid-generator", this.withLiga(payload));
    },

    emitCreateSale(payload) {
      this.$emit("create-sale", this.withLiga(payload));
    },

    emitRefreshBudget() {
      this.$emit("refresh-budget", this.requireActiveLigaId());
    },

    moneyDecimal(v) {
      return `${Number(v || 0).toLocaleString("es-ES", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 1,
      })} €`;
    },

    formatDate(v) {
      if (!v) return "-";

      const d = new Date(v);

      if (Number.isNaN(d.getTime())) return "-";

      return d.toLocaleDateString("es-ES", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      });
    },

    formatPoints(v) {
      const n = Number(v || 0);

      if (n > 0) return `+${n}`;

      return `${n}`;
    },

    async abrirHistoricoActivo(asset) {
      const assetId = asset && (asset.asset_id || asset.activo_id || asset.id);

      if (!assetId) {
        Swal.fire({
          icon: "error",
          title: "No se pudo cargar el histórico",
          text: "Este activo no tiene identificador.",
        });
        return;
      }

      this.selectedAsset = asset;
      this.historyDialog = true;
      this.loadingHistory = true;
      this.historyLoadingId = assetId;
      this.assetHistory = [];

      try {
        const ligaId = this.requireActiveLigaId();

        this.assetHistory = await this.store.fetchAssetHistory(
          assetId,
          ligaId
        );
      } catch (e) {
        Swal.fire({
          icon: "error",
          title: "No se pudo cargar el histórico",
          text: e.message || "Ha ocurrido un error inesperado.",
        });
      } finally {
        this.loadingHistory = false;
        this.historyLoadingId = null;
      }
    },

    money(v) {
      try {
        return new Intl.NumberFormat("es-ES", {
          style: "currency",
          currency: "EUR",
          maximumFractionDigits: 0,
        }).format(v);
      } catch (e) {
        return `${v}€`;
      }
    },
    abrirMapaActivo(asset) {
  if (!asset || asset.x == null || asset.y == null) {
    return;
  }

  this.selectedAsset = asset;
  this.mapDialog = true;

  this.$nextTick(() => {
    setTimeout(() => {
      this.pintarMapaActivo();
    }, 250);
  });
},

cerrarMapaActivo() {
  this.mapDialog = false;
  this.selectedAsset = null;

  if (this.assetMap) {
    this.assetMap.remove();
    this.assetMap = null;
  }

  this.assetMarker = null;
},

pintarMapaActivo() {
  const asset = this.selectedAsset;
  const el = this.$refs.assetMap;

  if (!asset || !el) return;

  const lng = Number(asset.x);
  const lat = Number(asset.y);

  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;

  if (this.assetMap) {
    this.assetMap.remove();
    this.assetMap = null;
  }

  this.assetMap = L.map(el, {
    zoomControl: true,
    zoomAnimation: false,
    fadeAnimation: false,
  }).setView([lat, lng], 9);

  const cartoApiKey = process.env.VUE_APP_CARTO_API_KEY;

  L.tileLayer(
    `https://{s}.basemaps.cartocdn.com/rastertiles/light_all/{z}/{x}/{y}.png?key=${cartoApiKey}`,
    {
      subdomains: "abcd",
      maxZoom: 20,
      attribution: "© OpenStreetMap, © CARTO",
    }
  ).addTo(this.assetMap);

  const color = this.colorTecnologia(asset.type);

  this.assetMarker = L.circleMarker([lat, lng], {
    radius: 12,
    color: "#ffffff",
    weight: 2,
    fillColor: color,
    fillOpacity: 1,
    className: "single-asset-marker",
  }).addTo(this.assetMap);

  this.assetMarker.bindPopup(`
    <div style="min-width: 200px;">
      <b>${asset.name || "Activo"}</b><br/>
      <b>Tecnología:</b> ${asset.type || "—"}<br/>
      <b>MW:</b> ${asset.capacity ?? "—"}<br/>
      <b>Municipio:</b> ${asset.municipio || "—"}<br/>
      <b>Provincia:</b> ${asset.provincia || "—"}
    </div>
  `).openPopup();

  setTimeout(() => {
    if (this.assetMap) {
      this.assetMap.invalidateSize();
      this.assetMap.setView([lat, lng], 9);
    }
  }, 150);
},

colorTecnologia(tecnologia) {
  const t = (tecnologia || "").toString().trim().toLowerCase();

  if (t.includes("solar") || t.includes("fotovolta")) return "#FDB813";
  if (t.includes("eólica") || t.includes("eolica")) return "#248F11";
  if (t.includes("hidro")) return "#4AA3DF";
  if (t.includes("nuclear")) return "#8E44AD";
  if (t.includes("ciclo") || t.includes("gas")) return "#E67E22";

  return "#757575";
},
  },
};
</script>

<style scoped>
.market-page {
  width: 100%;
  max-width: 100%;
  overflow: hidden;
}

.market-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.market-budget {
  font-size: 12px;
  color: #757575;
  white-space: nowrap;
}

.orbitron-title {
  font-family: "Orbitron", sans-serif !important;
  font-size: 20px;
  font-weight: 600;
}

.custom-tabs :deep(.v-tab) {
  color: #a7a4a4 !important;
}

.custom-tabs :deep(.v-tab--active) {
  color: #414141 !important;
  background-color: #f3e6b0;
}

.custom-tabs :deep(.v-tabs-slider) {
  background-color: #f3cd33 !important;
}

.market-content {
  max-width: 100%;
  overflow: hidden;
}

/* Reglas suaves para componentes internos */
.market-page :deep(.v-card),
.market-page :deep(.v-list),
.market-page :deep(.v-list-item) {
  max-width: 100%;
}

.market-page :deep(.v-card__actions),
.market-page :deep(.v-list-item__action) {
  flex-wrap: wrap;
  gap: 6px;
}

/* Tablet / iPad */
@media (max-width: 960px) {
  .orbitron-title {
    font-size: 18px;
  }

  .market-tabs :deep(.v-tabs-bar) {
    height: 42px !important;
  }

  .market-tabs :deep(.v-tab) {
    font-size: 12px !important;
    height: 42px !important;
    min-width: 0 !important;
  }

  .market-budget {
    font-size: 12px;
  }

  .market-page :deep(.v-btn) {
    font-size: 11px !important;
  }
}

/* Móvil */
@media (max-width: 600px) {
  .market-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
  }

  .orbitron-title {
    font-size: 16px;
  }

  .market-budget {
    font-size: 11px;
  }

  .market-tabs :deep(.v-tabs-bar) {
    height: 38px !important;
  }

  .market-tabs :deep(.v-tab) {
    font-size: 11px !important;
    height: 38px !important;
  }

  .market-page :deep(.v-card__actions) {
    display: grid !important;
    grid-template-columns: 1fr;
    gap: 6px;
  }

  .market-page :deep(.v-list-item) {
    align-items: flex-start;
  }

  .market-page :deep(.v-list-item__content) {
    min-width: 0;
  }

  .market-page :deep(.v-list-item__title),
  .market-page :deep(.v-list-item__subtitle) {
    white-space: normal;
    line-height: 1.25;
  }

  .market-page :deep(.v-btn) {
    width: 100%;
    min-height: 30px !important;
    font-size: 10px !important;
    margin: 0 !important;
  }
}

/* Móvil muy estrecho */
@media (max-width: 380px) {
  .market-page :deep(.v-btn) {
    font-size: 9px !important;
    min-height: 28px !important;
  }
}


.tab-label-with-notification {
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.sales-notification-dot {
  width: 9px;
  height: 9px;
  background-color: #25d366;
  border-radius: 50%;
  display: inline-block;
  margin-left: 3px;
  box-shadow: 0 0 0 2px white;
}
.asset-map-modal {
  width: 100%;
  height: 480px;
  border-radius: 14px;
  overflow: hidden;
  background: #eef2f5;
}

.asset-map-info {
  color: #414141;
  font-size: 13px;
}

:deep(.single-asset-marker) {
  filter:
    drop-shadow(0 0 6px rgba(255, 255, 255, 1))
    drop-shadow(0 0 14px rgba(49, 99, 95, 0.95));
}
.map-close-btn {
  width: 34px !important;
  height: 34px !important;
  min-width: 34px !important;
  background-color: #31635f !important;
  color: white !important;
  transition: all 0.2s ease;
}

.map-close-btn:hover {
  background-color: #244c49 !important;
  transform: scale(1.05);
}

.map-close-x {
  color: white !important;
  font-size: 26px;
  line-height: 1;
  font-weight: 500;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transform: translateY(-1px);
}
.modal-history-title {
  font-family: "Orbitron", sans-serif !important;
  color: #31635f;
  font-weight: 700;
}

.asset-history-points {
  font-family: "Orbitron", sans-serif !important;
  font-weight: 700;
}
.weekly-price-alert {
  display: flex;
  align-items: center;
  font-size: 12px;

  background-color: #f5faf7 !important;
  color: #287c2d !important;

  border: 1px solid #3a843f !important;
  border-radius: 8px !important;
}

.weekly-price-alert :deep(.v-alert__content) {
  display: flex;
  align-items: center;
  color: #3a843f !important;
}

.weekly-price-icon {
  flex: 0 0 auto;
  font-size: 20px !important;
  color: #3a843f !important;
  background-color: transparent !important;
}
</style>